// ─────────────────────────────────────────────
//  DepartureFlow — "Depart" button + modal sequence (D-06, D-07, D-08)
//  Eligibility rules come from src/domain/departure.js. The flow enforces:
//  every active room reassigned individually before a doctor departs; the
//  AIC hands off FIRST (server-side the Phase-3 rule authorizes the aicId
//  move); a CRNA's active room gets a takeover. Departed staff stay visible
//  but are filtered out of all dropdowns.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";
import Modal from "./Modal";
import useNowMinutes from "../hooks/useNowMinutes";
import { rosterDateKey } from "../hooks/useDailyRoster";
import Button from "./Button";
import {
  filterActiveStaff,
  activeRoomsFor,
  allRoomsReassigned,
  requiresAicHandoff,
  rosterDoctors,
} from "../domain/departure";
import {
  updateAssignment,
  setDailyRoster,
  setRosterMemberDeparted,
  notifyAIC,
  setRoomProvider,
} from "../firebase/firestore";

const { color, font, space, radius } = tokens;

// ⚠️ A REASSIGNMENT LIVES IN TWO DOCUMENTS, AND THE ROOM DOC IS THE ONE THAT
// DECIDES. This flow wrote only the assignment row, and
// `alerts.resolveRoomDoctorId` reads `room.supervisingDoctorId || assignment
// .attendingId` — the STALE ROOM DOC WINS. So after sending a doctor home:
//
//   · an emergency in one of their rooms was stamped with the DEPARTED doctor
//     as `roomDoctorId`, and `canClear` allows only that person, so the doctor
//     who actually holds the room could not clear it — and neither could the
//     AIC, because the AIC branch only runs when `roomDoctorId` is null;
//   · `emergencyRoomStaff` printed the departed doctor's name on every
//     responding phone as the person to look for.
//
// The CRNA takeover path had the identical hole via `resolveRoomCrnaId`, and
// `domain/channels` builds room chat membership from `room.providerId`, so the
// person who went home kept receiving the room's messages.
//
// `confirmRelief` already solved this for the relief path and says so in its
// header; this flow simply never used it. A row with no resolvable room is
// skipped rather than guessed at — a wrong room id here would rewrite the wrong
// room's staffing.
const syncRoomDoc = async (row, patch) => {
  const roomId = row?.roomId || null;
  if (!roomId) return;
  try {
    await setRoomProvider(roomId, patch);
  } catch (err) {
    // The assignment write is the clinical act and has already landed; a failed
    // mirror must not roll it back or block the departure. Reported, not thrown.
    console.error("Departure: could not sync the room doc:", err);
  }
};

export default function DepartureFlow({
  person,
  assignments = [],
  roster = {},
  aicId = null,
  facilityId,
  date,
  staffPool = { doctors: [], crnas: [] },
  actor,
  // Render the trigger as a compact ✕ instead of a full "Depart" button.
  // Opt-in, so the Daily List — where the action is the point of the row and
  // has space for a word — is unchanged.
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [reassignMap, setReassignMap] = useState({});
  const [handoffId, setHandoffId] = useState("");
  const [takeoverId, setTakeoverId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const departedMap = roster.departed || {};
  const isDoctor = person?.role === "anesthesiologist";
  const isAic = requiresAicHandoff(person, aicId);
  // ⚠️ THE CLOCK ONLY COUNTS ON TODAY'S BOARD. `useNowMinutes` is minutes since
  // THIS device's midnight, which says nothing about a board showing another
  // date — applying it to yesterday would release rooms because their cases
  // ended "hours ago", and to tomorrow because they have not started. On any
  // other date we hand in null, and `rowClaimsAt` then keeps every room, which
  // is the pre-existing behaviour and the fail-closed one.
  const nowMinutes = useNowMinutes();
  const nowMin = date === rosterDateKey() ? nowMinutes : null;
  const activeRooms = activeRoomsFor(person, assignments, nowMin);

  // Dropdowns NEVER include departed staff or the person leaving (D-06).
  // ⚠️ TODAY'S DOCTORS, NOT THE FACILITY'S. Owner, 2026-09-09: *"In this list
  // use the doctors from the roster of the day"*. Both pickers below offered
  // every anesthesiologist on staff, so the AIC could be handed to somebody who
  // is not in the building and a room could be reassigned to a doctor who never
  // arrives. `rosterDoctors` unions `supervisors` with the AIC — the same pair
  // `BoardPage.allowedDoctorIds` uses — and falls back to the full pool on a day
  // nobody rostered, which is what `doctorsFromRoster` reports so the modal can
  // say it out loud instead of quietly widening.
  //
  // ⚠️ AND THE LAST WORD IS "NEVER AN EMPTY PICKER". `rosterDoctors` widens for a
  // day nobody rostered, but the two filters BELOW it can still empty the list —
  // the commonest case being a roster whose only doctor is the one departing,
  // which is exactly the lone AIC who most needs to hand off. An empty select
  // with a disabled confirm button strands them on the board all night. So the
  // roster answer is taken only if somebody survives both filters.
  const pickCandidates = (pool) =>
    filterActiveStaff(pool, departedMap).filter((d) => d.id !== person?.id);
  // ⚠️ THE AIC ARRIVES TWICE AND THE PROP IS THE AUTHORITY. This component takes
  // `aicId` separately from `roster`, and a caller may hand a roster object that
  // does not carry it. Reading only `roster.aicId` dropped today's AIC out of
  // the per-room picker on exactly those callers — the one doctor who is
  // certainly in the building, missing from the list of who can take a room.
  const fromRosterPool = rosterDoctors(staffPool.doctors, {
    ...roster,
    aicId: aicId || roster?.aicId || null,
  });
  let doctorCandidates = pickCandidates(fromRosterPool.doctors);
  let doctorsFromRoster = fromRosterPool.fromRoster;
  if (doctorCandidates.length === 0 && doctorsFromRoster) {
    doctorCandidates = pickCandidates(staffPool.doctors);
    doctorsFromRoster = false;
  }
  const crnaCandidates = filterActiveStaff(staffPool.crnas, departedMap)
    .filter((c) => c.id !== person?.id);

  const reset = () => {
    setOpen(false);
    setReassignMap({});
    setHandoffId("");
    setTakeoverId("");
    setError("");
  };

  const markDeparted = async () => {
    await setRosterMemberDeparted(facilityId, date, person.id, true, actor?.uid);
    try {
      await notifyAIC({
        facilityId,
        actor: { uid: actor?.uid, name: actor?.name },
        summary: `marked ${person.name} as departed for the day`,
      });
    } catch (notifyErr) {
      console.error("Notify AIC failed:", notifyErr); // best-effort
    }
  };

  const confirmSimple = async () => {
    setBusy(true);
    setError("");
    try {
      await markDeparted();
      reset();
    } catch (err) {
      console.error("Departure failed:", err);
      setError("Couldn't save. Check your connection and try again.");
      setBusy(false);
    }
  };

  const confirmCrnaTakeover = async () => {
    setBusy(true);
    setError("");
    try {
      const takeover = crnaCandidates.find((c) => c.id === takeoverId);
      // The takeover CRNA inherits each of this person's live rooms (D-08).
      for (const room of activeRooms) {
        await updateAssignment(room.id, {
          ahpId: takeover.id,
          ahpName: takeover.name || "",
        });
        await syncRoomDoc(room, {
          providerId: takeover.id,
          providerName: takeover.name || "",
        });
      }
      await markDeparted();
      reset();
    } catch (err) {
      console.error("Takeover departure failed:", err);
      setError("Couldn't save. Check your connection and try again.");
      setBusy(false);
    }
  };

  const confirmDoctorDeparture = async () => {
    setBusy(true);
    setError("");
    try {
      // 1) AIC handoff FIRST — the Phase-3 rule only lets the current AIC
      //    (or an admin) move aicId, so this write must precede departure.
      if (isAic) {
        const successor = doctorCandidates.find((d) => d.id === handoffId);
        await setDailyRoster(
          facilityId,
          date,
          { aicId: successor.id, aicName: successor.name || "" },
          actor?.uid
        );
      }
      // 2) Every room reassigned individually (D-07).
      for (const room of activeRooms) {
        const replacement = doctorCandidates.find((d) => d.id === reassignMap[room.id]);
        await updateAssignment(room.id, {
          attendingId: replacement.id,
          attendingName: replacement.name || "",
        });
        await syncRoomDoc(room, {
          supervisingDoctorId: replacement.id,
          supervisorName: replacement.name || "",
        });
      }
      // 3) Only then mark departed.
      await markDeparted();
      reset();
    } catch (err) {
      console.error("Doctor departure failed:", err);
      setError("Couldn't save. Check your connection and try again.");
      setBusy(false);
    }
  };

  // ── Modal variants per the UI-SPEC Destructive Action Confirmations table ──
  let modal = null;
  if (open) {
    if (isDoctor && (isAic || activeRooms.length > 0)) {
      const canConfirm =
        (!isAic || !!handoffId) && allRoomsReassigned(reassignMap, activeRooms);
      modal = (
        <Modal
          isOpen
          onClose={reset}
          title={isAic ? "Hand Off AIC Before Departing" : `Reassign ${person.name}'s Rooms`}
          maxWidth={480}
          footer={
            <>
              <Button variant="secondary" onClick={reset} disabled={busy}>Cancel</Button>
              <Button variant="danger" disabled={!canConfirm || busy}
                onClick={confirmDoctorDeparture}>
                {isAic ? "Hand Off & Depart" : "Confirm Departure"}
              </Button>
            </>
          }
        >
          {isAic && (
            <div style={st.block}>
              <p style={st.copy}>
                You are the AIC. Choose who takes over, then you can mark yourself as departed.
              </p>
              {/* ⚠️ A WIDER LIST THAN ASKED FOR HAS TO SAY SO. Silently showing
                  every doctor on staff is exactly the behaviour the owner
                  reported; if the day has no roster we still have to let the
                  AIC hand off, so the list widens and admits it. */}
              {!doctorsFromRoster && (
                <p role="note" style={st.copy}>
                  No doctors are on today&rsquo;s roster, so every staff doctor is listed.
                  Check the person you pick is actually here.
                </p>
              )}
              <label htmlFor="aic-handoff-select" style={st.label}>Hand off AIC to</label>
              <select
                id="aic-handoff-select"
                value={handoffId}
                onChange={(e) => setHandoffId(e.target.value)}
                style={st.select}
              >
                <option value="">Pick a doctor…</option>
                {doctorCandidates.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.id === aicId ? " (AIC)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {activeRooms.length > 0 && (
            <div style={st.block}>
              <p style={st.copy}>
                {`${person.name} is covering ${activeRooms.length} room${activeRooms.length === 1 ? "" : "s"}. Reassign each room before confirming departure.`}
              </p>
              {activeRooms.map((room) => (
                <div key={room.id} style={st.roomRow}>
                  <span style={st.roomLabel}>OR {room.orRoom || room.roomName || room.id}</span>
                  <select
                    aria-label={`Reassign OR ${room.orRoom || room.roomName || room.id}`}
                    value={reassignMap[room.id] || ""}
                    onChange={(e) =>
                      setReassignMap((prev) => ({ ...prev, [room.id]: e.target.value }))
                    }
                    style={{ ...st.select, flex: 1 }}
                  >
                    <option value="">Pick a doctor…</option>
                    {doctorCandidates.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
          {error && <p style={st.error} role="alert">{error}</p>}
        </Modal>
      );
    } else if (!isDoctor && activeRooms.length > 0) {
      modal = (
        <Modal
          isOpen
          onClose={reset}
          title={`Reassign ${person.name}'s Room?`}
          maxWidth={420}
          footer={
            <>
              <Button variant="secondary" onClick={reset} disabled={busy}>Cancel</Button>
              <Button variant="danger" disabled={!takeoverId || busy} onClick={confirmCrnaTakeover}>
                Assign Takeover
              </Button>
            </>
          }
        >
          <p style={st.copy}>
            {`Someone must take over ${activeRooms.map((r) => `OR ${r.orRoom || r.roomName || r.id}`).join(", ")} before ${person.name} can leave.`}
          </p>
          <label htmlFor="takeover-select" style={st.label}>Takeover CRNA</label>
          <select
            id="takeover-select"
            value={takeoverId}
            onChange={(e) => setTakeoverId(e.target.value)}
            style={st.select}
          >
            <option value="">Pick a CRNA…</option>
            {crnaCandidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.crossFacility && c.facilityName ? ` · ${c.facilityName}` : ""}
              </option>
            ))}
          </select>
          {error && <p style={st.error} role="alert">{error}</p>}
        </Modal>
      );
    } else {
      modal = (
        <Modal
          isOpen
          onClose={reset}
          title={`Mark ${person.name} as Departed?`}
          maxWidth={420}
          footer={
            <>
              <Button variant="secondary" onClick={reset} disabled={busy}>Cancel</Button>
              <Button variant="danger" disabled={busy} onClick={confirmSimple}>
                Mark as Departed
              </Button>
            </>
          }
        >
          <p style={st.copy}>
            {!isDoctor
              ? `${person.name}'s room is done. They will be removed from today's assignments.`
              : `${person.name} will be removed from today's assignments.`}
          </p>
          {error && <p style={st.error} role="alert">{error}</p>}
        </Modal>
      );
    }
  }

  return (
    <>
      <span style={st.touchWrap}>
        {compact ? (
          // ⚠️ ICON-ONLY, SO THE LABEL MOVES RATHER THAN DISAPPEARS. `ariaLabel`
          // and `title` still name the person and the action — a bare ✕ with no
          // accessible name is unreadable to a screen reader and ambiguous to
          // everyone else ("close? remove? cancel?").
          //
          // It also gives back the alarm budget. Four saturated red buttons on a
          // card explicitly labelled "reference · not an alarm" were the loudest
          // thing in that column, competing with the strips that mean somebody
          // is in trouble.
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`Mark ${person?.name || "person"} as departing`}
            title={`Depart ${person?.name || ""}`.trim()}
            style={st.compactBtn}
          >
            ✕
          </button>
        ) : (
          <Button variant="danger" size="sm" onClick={() => setOpen(true)}
            ariaLabel={`Mark ${person?.name || "person"} as departing`}>
            Depart
          </Button>
        )}
      </span>
      {modal}
    </>
  );
}

const st = {
  // 28px keeps it a comfortable pointer target while staying out of the way;
  // the border carries the destructive meaning without a saturated fill.
  compactBtn: {
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "inherit",
    lineHeight: 1,
    // ⚠️ RED GLYPH, NEUTRAL BOX — and the box is the half that had to go.
    // A red outline is a filled shape at a glance, so four of them stacked down
    // the MD Call List were the loudest thing in a card whose own header says
    // "reference · not an alarm", competing with the strips that mean somebody
    // is actually in trouble. The glyph stays red because the ACTION is still
    // destructive; the border stops shouting on its behalf. Alarm colour is a
    // finite budget: spend it on the alarm.
    //
    // ⚠️ BUT NOT `border.default` — THAT WAS A 1.4.11 FAILURE, MEASURED. This
    // border is the ONLY thing marking the control's boundary now that the fill
    // and the red outline are gone, so it is a UI component boundary and owes
    // 3:1. `border.default` (#b9c6d8) is 1.73:1 on white — it is the app's card
    // hairline, never engineered to carry a control on its own. `text.muted` is
    // 6.40:1 and still reads as quiet.
    color: color.accent.error,
    background: "transparent",
    border: `1px solid ${color.text.muted}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    padding: 0,
  },
  // 44px minimum touch target on mobile per the UI-SPEC exception rule.
  touchWrap: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
  },
  block: { marginBottom: space.md },
  copy: {
    margin: `0 0 ${space.sm}px`,
    fontSize: font.size.body,
    color: color.text.primary,
    lineHeight: font.lineHeight.normal,
  },
  label: {
    display: "block",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    marginBottom: space.xs,
  },
  select: {
    width: "100%",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "10px 12px",
    fontSize: font.size.body,
    fontFamily: font.family,
    boxSizing: "border-box",
  },
  roomRow: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    marginBottom: space.sm,
  },
  roomLabel: {
    width: 70,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    flexShrink: 0,
  },
  error: {
    fontSize: font.size.label,
    color: color.accent.error,
    marginTop: space.sm,
  },
};
